import { createApp, nextTick } from 'vue'
import App from './App.vue'
import { vuetify } from './plugins/vuetify'
import router from './router'
import { i18n } from './i18n'
import { hasCookieConsentCategory, runCookieConsent } from './lib/cookie-consent'
import './main.scss'

const app = createApp(App).use(vuetify).use(router).use(i18n)
let trackingConsentState = { analytics: false, marketing: false }
let internalClickTrackingInitialized = false

type AnalyticsModule = typeof import('./lib/analytics')
type InternalAnalyticsModule = typeof import('./lib/internal-analytics')
type TelemetryModule = typeof import('./lib/telemetry')

let analyticsModulePromise: Promise<AnalyticsModule> | null = null
let internalAnalyticsModulePromise: Promise<InternalAnalyticsModule> | null = null
let telemetryModulePromise: Promise<TelemetryModule> | null = null

function getAnalyticsModule() {
  analyticsModulePromise ||= import('./lib/analytics')
  return analyticsModulePromise
}

function getInternalAnalyticsModule() {
  internalAnalyticsModulePromise ||= import('./lib/internal-analytics')
  return internalAnalyticsModulePromise
}

function getTelemetryModule() {
  telemetryModulePromise ||= import('./lib/telemetry')
  return telemetryModulePromise
}

function runWhenIdle(callback: () => void, timeout = 5000) {
  const idleWindow = window as Window & {
    requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number
  }

  if (idleWindow.requestIdleCallback) {
    idleWindow.requestIdleCallback(callback, { timeout })
    return
  }

  globalThis.setTimeout(callback, timeout)
}

function reportClientEventLazy(type: string, payload: Record<string, unknown>, severity: 'info' | 'warning' | 'error') {
  void getTelemetryModule()
    .then(({ reportClientEvent }) => reportClientEvent(type, payload, severity))
    .catch(() => undefined)
}

function toErrorPayload(error: unknown) {
  if (error instanceof Error) {
    return {
      message: error.message,
      stack: error.stack,
      name: error.name,
    }
  }

  return {
    message: typeof error === 'string' ? error : 'Unknown error',
    stack: undefined,
    name: 'UnknownError',
  }
}

app.config.errorHandler = (error, instance, info) => {
  const payload = toErrorPayload(error)
  console.error('vue-error', {
    ...payload,
    info,
    component: instance?.$options?.name || 'AnonymousComponent',
  })
  reportClientEventLazy('frontend_error', {
    ...payload,
    info,
    component: instance?.$options?.name || 'AnonymousComponent',
  }, 'error')
}

window.addEventListener('unhandledrejection', (event) => {
  const payload = toErrorPayload(event.reason)
  console.error('unhandled-rejection', payload)
  reportClientEventLazy('unhandled_rejection', payload, 'error')
})

app.mount('#app')
runWhenIdle(() => {
  void getTelemetryModule()
    .then(({ startWebVitalsTracking }) => startWebVitalsTracking())
    .catch(() => undefined)
}, 7000)

router.afterEach((to) => {
  if (!trackingConsentState.analytics && !trackingConsentState.marketing) return

  void nextTick(() => {
    requestAnimationFrame(() => {
      const pageTitle = document?.title || ''
      void Promise.all([getAnalyticsModule(), getInternalAnalyticsModule()])
        .then(([analyticsModule, internalAnalyticsModule]) => {
          analyticsModule.trackPageView(to.fullPath, pageTitle)
          internalAnalyticsModule.trackInternalPageView(to.fullPath, pageTitle)
        })
        .catch(() => undefined)
    })
  })
})

nextTick(() => {
  const syncTrackingConsent = async () => {
    const [analytics, marketing] = await Promise.all([
      hasCookieConsentCategory('analytics'),
      hasCookieConsentCategory('marketing'),
    ])
    trackingConsentState = { analytics, marketing }

    if (!analytics && !marketing) {
      if (internalAnalyticsModulePromise) {
        void internalAnalyticsModulePromise
          .then((internalAnalyticsModule) => {
            internalAnalyticsModule.setInternalAnalyticsConsent({ analytics, marketing })
          })
          .catch(() => undefined)
      }

      return
    }

    void Promise.all([getAnalyticsModule(), getInternalAnalyticsModule()])
      .then(([analyticsModule, internalAnalyticsModule]) => {
        internalAnalyticsModule.setInternalAnalyticsConsent({ analytics, marketing })
        if (!internalClickTrackingInitialized) {
          internalAnalyticsModule.initInternalClickTracking()
          internalClickTrackingInitialized = true
        }
        return analyticsModule.setTrackingConsent({ analytics, marketing }).then(() => {
          const pageTitle = document?.title || ''
          analyticsModule.trackPageView(router.currentRoute.value.fullPath, pageTitle)
          internalAnalyticsModule.trackInternalPageView(router.currentRoute.value.fullPath, pageTitle)
        })
      })
      .catch(() => undefined)
  }

  void runCookieConsent({
    disablePageInteraction: true,
    guiOptions: {
      consentModal: { layout: 'box', position: 'bottom right', flipButtons: false },
      preferencesModal: { layout: 'box', position: 'right' }
    },
    categories: {
      necessary: { readOnly: true, enabled: true },
      analytics: {},
      marketing: {
        autoClear: {
          cookies: [{ name: /^_ga/ }],
        },
      },
    },
    onConsent: syncTrackingConsent,
    onChange: syncTrackingConsent,
    language: {
      default: 'de',
      translations: {
        de: {
          consentModal: {
            title: 'Cookies bei LINQFORCE',
            description: 'Wir verwenden Cookies, um unsere Website zuverlässig zu betreiben und, mit Ihrer Zustimmung, Reichweite und Marketing zu verbessern. Notwendige Cookies sind immer aktiv.',
            footer: 'Details in&nbsp;<a href="/datenschutz" class="cc__link">Datenschutzerklärung</a>&nbsp;und&nbsp;<a href="/impressum" class="cc__link">Impressum</a>.',
            acceptAllBtn: 'Alle empfohlenen Cookies akzeptieren',
            acceptNecessaryBtn: 'Nur notwendige Cookies akzeptieren',
          },
          preferencesModal: {
            title: 'Datenschutzeinstellungen',
            closeIconLabel: 'Schließen',
            savePreferencesBtn: 'Auswahl speichern',
            sections: [
              { title: 'Notwendige Cookies', description: 'Erforderlich für Grundfunktionen.', linkedCategory: 'necessary' },
              { title: 'Analytics', description: 'Optionale Reichweitenmessung.', linkedCategory: 'analytics' },
              { title: 'Marketing', description: 'Optionale Marketing-Tools.', linkedCategory: 'marketing' },
            ],
          }
        },
        en: {
          consentModal: {
            title: 'Cookies at LINQFORCE',
            description: 'We use cookies to operate our website reliably and, with your consent, improve analytics and marketing. Necessary cookies are always active.',
            footer: 'Details in&nbsp;<a href="/datenschutz" class="cc__link">privacy policy</a>&nbsp;and&nbsp;<a href="/impressum" class="cc__link">legal notice</a>.',
            acceptAllBtn: 'Accept all recommended cookies',
            acceptNecessaryBtn: 'Accept only necessary cookies',
          },
          preferencesModal: {
            title: 'Privacy settings',
            closeIconLabel: 'Close',
            savePreferencesBtn: 'Save selection',
            sections: [
              { title: 'Necessary cookies', description: 'Required for core functionality.', linkedCategory: 'necessary' },
              { title: 'Analytics', description: 'Optional audience measurement.', linkedCategory: 'analytics' },
              { title: 'Marketing', description: 'Optional marketing tools.', linkedCategory: 'marketing' },
            ],
          },
        }
      }
    }
  })

  void syncTrackingConsent()
})
